import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import moment from "moment";
import {
  ChevronDown,
  Clock,
  Droplets,
  Footprints,
  LucideUtensils,
  Moon,
} from "lucide-react";

import Button from "../../Button";
import Card from "../../Card/Card";

import { useAppSelector } from "src/redux/hooks";
import { useAppDispatch } from "src/redux/hooks";
import * as actions from "src/redux/slices/configSlice";
import { loggers } from "src/types/config.types";
import Shimmer from "src/components/common/Shimmer";
import { bindQueryParams } from "src/helpers/tools";
import { LOGGERS_TYPES } from "src/constants/loggers";
import { EVENT_NAMES, SCREEN_NAMES } from "src/helpers/analytics/events";
import analytics from "src/helpers/analytics";

const content = {
  water_data: {
    icon: <Droplets className="w-4 h-4" />,
    title: "Water",
    // color: "#3b82f6",
    loggerId: "water-logs",
  },
  steps_data: {
    icon: <Footprints className="w-4 h-4" />,
    title: "Steps",
    // color: "#10b981",
    loggerId: "steps-logs",
  },
  sleep_data: {
    icon: <Moon className="w-4 h-4" />,
    title: "Sleep",
    // color: "#8b5cf6",
    loggerId: "sleep-logs",
  },
  diet_data: {
    icon: <LucideUtensils className="w-4 h-4" />,
    title: "Diet",
    // color: "#3b82f6",
    loggerId: "diet-logs",
  },
};

interface TodaysRemindersProps {
  id: string;
  title: string;
  description: string;
  type: string;
  supportProgramKey: string;
}

const TodaysReminders: React.FC<TodaysRemindersProps> = ({
  id,
  title,
  description,
  type,
  supportProgramKey,
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const dispatch = useAppDispatch();
  const { patient } = useAppSelector((state) => state.auth);
  const { reminders } = useAppSelector((state) => state.config);
  const navigate = useNavigate();
  const params = useParams();

  useEffect(() => {
    if (patient?.data?.patient_id) {
      getReminders();
    }
  }, [patient]);

  const getReminders = () => {
    new Promise((resolve, reject) => {
      dispatch(
        actions.getRemindersRequest({
          payload: {
            user_id: patient?.data?.patient_id,
            current_date: moment().format("YYYY-MM-DD"),
          },
          resolve,
          reject,
        })
      );
    });
  };

  const handleStartLoggingClick = (loggerId: string) => {
    const loggerType = LOGGERS_TYPES[loggerId as keyof typeof LOGGERS_TYPES];
    const url = bindQueryParams(
      `/app/${supportProgramKey}/health-assistant-screen`,
      {
        loggerType,
      }
    );
    navigate(url);
  };

  const isLoading = reminders?.isLoading;

  return (
    <div className="mx-4">
      <Card
        title={title}
        icon={<Clock className="w-4 h-4" />}
        description={description}
      >
        <div className="relative w-full">
          <div
            className="flex flex-col"
            style={{
              maxHeight: isExpanded || isLoading ? "100%" : "100px",
              overflow: "hidden",
            }}
          >
            {isLoading ? (
              <div className="flex flex-col gap-4 mt-4">
                {Array.from({ length: 3 }).map((_, index) => (
                  <div className="flex flex-row gap-2" key={index.toString()}>
                    <Shimmer
                      width="32px"
                      height="32px"
                      rounded="rounded-full"
                    />
                    <div className="flex flex-col gap-2">
                      <Shimmer width="100px" height="12px" />
                      <Shimmer width="200px" height="12px" />
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              Object.keys(content).map((key, index) => {
                const contentData = content[key as keyof typeof content];
                const loggerData =
                  reminders?.data?.loggers_data?.[key as loggers];
                const acheivedValue = loggerData?.acheived_value;
                const goalValue = loggerData?.goal_value;
                const unit = loggerData?.unit;

                const loggerName = contentData.title
                  .toLowerCase()
                  .replace(" ", "-");

                const handleClick = (type: "set" | "log") => {
                  // Navigate to log details screen
                  if (type === "set") {
                    // Event: SET_GOAL_CLICKED
                    analytics.trackEvent(
                      `${loggerName}_${EVENT_NAMES.SET_GOAL_CLICKED}`,
                      {
                        card_title: title,
                        screen_name: SCREEN_NAMES.HOME_SCREEN,
                        support_program_key: supportProgramKey,
                      }
                    );
                    navigate(
                      `/app/${supportProgramKey}/log-details-screen?log=${contentData.loggerId}?openModal=set-goals`
                    );
                  } else {
                    // Event: LOG_NOW_CLICKED
                    analytics.trackEvent(
                      `${loggerName}_${EVENT_NAMES.LOG_NOW_CLICKED}`,
                      {
                        card_title: title,
                        screen_name: SCREEN_NAMES.HOME_SCREEN,
                        support_program_key: supportProgramKey,
                      }
                    );

                    // Navigate to log details screen
                    handleStartLoggingClick(contentData.loggerId);
                  }
                };

                return (
                  <div
                    key={key}
                    className="flex items-center justify-between p-2 hover:bg-gray-50 rounded-lg cursor-pointer transition-colors"
                  >
                    <div className="flex flex-row items-center gap-2">
                      <div className="text-[var(--app-primary)] bg-[var(--icon-bg-primary)] rounded-full p-2">
                        {contentData.icon}
                      </div>
                      <div className="flex flex-col">
                        <span className="text-sm font-medium">
                          {contentData.title}
                        </span>
                        <span className="text-xs text-gray-500">
                          {`${acheivedValue} / ${goalValue} ${unit}`}
                        </span>
                      </div>
                    </div>
                    <div className="flex flex-row items-center gap-2">
                      <Button
                        size="sm"
                        variant={
                          loggerData?.is_first ? "contained" : "outlined"
                        }
                        rounded="full"
                        onClick={() =>
                          handleClick(loggerData?.is_first ? "set" : "log")
                        }
                      >
                        {loggerData?.is_first ? "Set Goal" : "Log Now"}
                      </Button>
                    </div>
                  </div>
                );
              })
            )}
          </div>

          {!isLoading && (
            <div className="px-4 pt-1 flex items-center justify-center">
              <button
                type="button"
                className="p-2 rounded-full transition-colors bg-gray-100 hover:bg-gray-200 border border-gray-200"
                onClick={() => setIsExpanded(!isExpanded)}
              >
                <ChevronDown
                  className={`w-5 h-5 text-black ${
                    isExpanded ? "rotate-180" : ""
                  } transition-all`}
                />
              </button>
            </div>
          )}
        </div>
      </Card>
    </div>
  );
};

export default TodaysReminders;
